import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { ArrowLeft, Eye, EyeOff, Copy, KeyRound, Search, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AUTO_HIDE_MS = 30000;

export default function SupervisorGroupLogins({ token, onBack }) {
  const [rows, setRows] = useState([]);
  const [division, setDivision] = useState("");
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [revealed, setRevealed] = useState({});

  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/supervisor/group-logins`, { headers });
      setRows(res.data?.groups || []);
      setDivision(res.data?.division || "");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load group logins");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (Object.keys(revealed).length === 0) return;
    const tick = setInterval(() => {
      const now = Date.now();
      setRevealed(prev => {
        const next = {};
        for (const [k, exp] of Object.entries(prev)) if (exp > now) next[k] = exp;
        return Object.keys(next).length === Object.keys(prev).length ? prev : next;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [revealed]);

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  const filtered = rows.filter(r => {
    const lc = search.trim().toLowerCase();
    if (!lc) return true;
    return r.group_code.toLowerCase().includes(lc) || (r.group_name || "").toLowerCase().includes(lc);
  });

  return (
    <div className="space-y-4" data-testid="sv-group-logins-view">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack} data-testid="sv-group-logins-back">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <div>
            <h2 className="font-bold text-lg flex items-center gap-2 text-gray-900">
              <KeyRound className="h-5 w-5 text-rose-500" />
              Group Logins — {division}
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Your division's group logins for subs. Passwords auto-hide after 30s.
            </p>
          </div>
        </div>
        <Button variant="outline" size="icon" onClick={load} disabled={loading} data-testid="sv-group-logins-refresh">
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      <div className="relative max-w-xs">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
        <Input
          placeholder="Search group…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-8"
          data-testid="sv-group-logins-search"
        />
      </div>

      {loading && <p className="text-sm text-gray-500 italic py-8 text-center">Loading…</p>}
      {!loading && filtered.length === 0 && (
        <p className="text-sm text-gray-500 italic py-8 text-center">No groups found.</p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.map(r => {
          const shown = !!revealed[r.group_code];
          return (
            <Card key={r.group_code} className="border-gray-200" data-testid={`sv-group-login-card-${r.group_code}`}>
              <CardContent className="p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-bold text-sm text-gray-900">{r.group_code}</span>
                    <span className="text-xs text-gray-500 ml-1.5">{r.group_name}</span>
                  </div>
                  {r.password_set && (
                    <Badge className="bg-emerald-100 text-emerald-800 text-[9px]">custom pw</Badge>
                  )}
                </div>

                <div className="rounded-md bg-gray-50 border border-gray-200 p-2 space-y-1">
                  <p className="text-[10px] font-bold text-gray-500 uppercase">Head Counselor</p>
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-mono">{r.head_username}</span>
                    <button onClick={() => copyText(r.head_username)} className="text-gray-400 hover:text-gray-600" data-testid={`sv-copy-head-user-${r.group_code}`}>
                      <Copy className="h-3 w-3" />
                    </button>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-mono" data-testid={`sv-head-pw-${r.group_code}`}>
                      {shown ? r.head_password : "••••••••"}
                    </span>
                    <div className="flex items-center gap-1.5">
                      {shown && (
                        <button onClick={() => copyText(r.head_password)} className="text-gray-400 hover:text-gray-600" data-testid={`sv-copy-head-pw-${r.group_code}`}>
                          <Copy className="h-3 w-3" />
                        </button>
                      )}
                      <button
                        onClick={() => setRevealed(prev => {
                          const next = { ...prev };
                          if (shown) delete next[r.group_code];
                          else next[r.group_code] = Date.now() + AUTO_HIDE_MS;
                          return next;
                        })}
                        className="text-gray-400 hover:text-gray-700"
                        data-testid={`sv-reveal-${r.group_code}`}
                      >
                        {shown ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                      </button>
                    </div>
                  </div>
                </div>

                {r.assistant_username && (
                  <div className="rounded-md bg-gray-50 border border-gray-200 p-2 space-y-1">
                    <p className="text-[10px] font-bold text-gray-500 uppercase">Assistant</p>
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-mono">{r.assistant_username}</span>
                      <button onClick={() => copyText(r.assistant_username)} className="text-gray-400 hover:text-gray-600">
                        <Copy className="h-3 w-3" />
                      </button>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-mono">{r.assistant_password}</span>
                      <button onClick={() => copyText(r.assistant_password)} className="text-gray-400 hover:text-gray-600">
                        <Copy className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
